import { useState } from "react";
import {
  Button,
  ButtonProps,
  Grid,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";

export type PaginateVariant = "default" | "stepper" | "small-table";

export type PaginateProps = {
  pageSize: number;
  pageSizeItems?: { pageSize: number; url: URL }[];
  prevUrl?: URL;
  nextUrl?: URL;
  variant?: PaginateVariant;
  sx?: SxProps<Theme>;
};

export function Paginate({
  pageSizeItems = [],
  pageSize,
  prevUrl,
  nextUrl,
  variant = "default",
  sx,
}: PaginateProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));

  const handleClick: ButtonProps["onClick"] = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const prevNextButton = (url?: URL, buttonProps: ButtonProps = {}) => {
    return (
      <Button
        component={url ? RouterLink : "button"}
        to={url?.href}
        disabled={!Boolean(url)}
        {...buttonProps}
      />
    );
  };

  const prevIcon = <ChevronLeftIcon fontSize="small" />;
  const nextIcon = <ChevronRightIcon fontSize="small" />;
  const isStepper = variant === "stepper";
  const isSmallTable = variant === "small-table";

  const prevText = isStepper ? "Previous" : "Prev";
  const nextText = "Next";
  const prevVariant: ButtonProps["variant"] = isStepper ? "outlined" : "text";
  const nextVariant: ButtonProps["variant"] = isStepper ? "contained" : "text";
  const prevColor: ButtonProps["color"] = isStepper ? "primary" : undefined;
  const nextColor: ButtonProps["color"] = isStepper ? "primary" : undefined;
  const prevButtonProps: ButtonProps = {
    variant: prevVariant,
    color: prevColor,
    startIcon: prevIcon,
    children: prevText,
    sx: { py: isSmallTable ? 0 : 1 },
  };
  const nextButtonProps: ButtonProps = {
    variant: nextVariant,
    color: nextColor,
    endIcon: nextIcon,
    children: nextText,
    sx: { py: isSmallTable ? 0 : 1 },
  };

  const prevButton = prevNextButton(prevUrl, prevButtonProps);
  const nextButton = prevNextButton(nextUrl, nextButtonProps);

  return (
    <Grid
      container
      sx={[
        {
          py: isStepper ? 2 : 0,
          px: isSmallTable ? "12px" : isStepper ? 2 : 3,
          display: "flex",
          alignItems: "center",
          justifyContent: isSmallTable ? "end" : "space-between",
          backgroundColor: isSmallTable ? "transparent" : "background.light",
          borderRadius: "0 0 4px 4px",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {pageSizeItems.length > 1 && !isSmallTable && (
        <Grid
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            pb: { xs: 1, sm: 0 },
            gap: 1,
          }}
          size={{
            xs: 12,
            sm: "auto",
          }}
        >
          <Typography
            id="page-size-select-label"
            variant="caption"
            sx={{ color: "text.secondary" }}
          >
            Rows per page
          </Typography>
          <Button
            variant="text"
            sx={{
              my: 0.5,
              fontSize: 16,
              fontWeight: 700,
              width: "45px",
              minWidth: "initial",
              "& .MuiButton-endIcon": { marginLeft: 0 },
              "&:hover": { backgroundColor: "transparent" },
            }}
            endIcon={
              open ? (
                <ArrowDropUpIcon fontSize="inherit" />
              ) : (
                <ArrowDropDownIcon fontSize="inherit" />
              )
            }
            onClick={handleClick}
          >
            {pageSize}
          </Button>
          <Menu
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            anchorEl={anchorEl}
            id="page-size-select"
            sx={{
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
              },
            }}
          >
            {pageSizeItems.map((item) => (
              <MenuItem
                key={item.url.href}
                {...{ component: RouterLink, to: item.url.href }}
              >
                {item.pageSize}
              </MenuItem>
            ))}
          </Menu>
        </Grid>
      )}
      <Grid
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "center" : "flex-end",
        }}
        gap={isStepper ? 2 : 0}
        size={{
          xs: 12,
          sm: "auto",
        }}
      >
        {prevButton}
        {nextButton}
      </Grid>
    </Grid>
  );
}
